import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service layer for audit trail operations
 * Handles logging and querying of modification history
 */

export const auditService = {
  /**
   * Log an audit event manually
   * @param {object} auditData - Audit event data
   * @returns {Promise<{data, error}>}
   */
  async logEvent(auditData) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      const auditEntry = {
        user_id: user?.id,
        action_type: auditData?.actionType,
        resource_type: auditData?.resourceType,
        resource_id: auditData?.resourceId,
        changes: auditData?.changes || {},
        metadata: auditData?.metadata || {},
        ip_address: auditData?.ipAddress,
        user_agent: auditData?.userAgent,
        created_at: new Date()?.toISOString()
      };
      
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert([auditEntry])
        ?.select()
        ?.single();
      
      if (error) {
        logger?.error('Failed to log audit event', error, { requestId });
        return { data: null, error };
      }
      
      return { data, error: null };
    } catch (error) {
      logger?.error('Audit logging error', error, { requestId });
      return { data: null, error: { message: 'Failed to log audit event' } };
    }
  },

  /**
   * Get audit logs with filtering
   * @param {object} options - Query options
   * @returns {Promise<{data, error}>}
   */
  async getAuditLogs(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      let query = supabase
        ?.from('audit_logs')
        ?.select(`
          *,
          user:user_id(id, email, raw_user_meta_data)
        `)
        ?.order('created_at', { ascending: false });
      
      // Filter by action type
      if (options?.actionType && options?.actionType !== 'all') {
        query = query?.eq('action_type', options?.actionType);
      }
      
      // Filter by resource type
      if (options?.resourceType && options?.resourceType !== 'all') {
        query = query?.eq('resource_type', options?.resourceType);
      }
      
      // Filter by date range
      if (options?.dateRange?.start) {
        query = query?.gte('created_at', options?.dateRange?.start);
      }
      if (options?.dateRange?.end) {
        query = query?.lte('created_at', options?.dateRange?.end);
      }
      
      // Filter by specific user (for caregivers viewing managed profiles)
      if (options?.userId) {
        query = query?.eq('user_id', options?.userId);
      }
      
      // Pagination
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }
      if (options?.offset) {
        query = query?.range(options?.offset, options?.offset + (options?.limit || 50) - 1);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger?.error('Failed to fetch audit logs', error, { requestId });
        return { data: null, error };
      }
      
      // Transform data to camelCase
      const transformedData = data?.map(log => ({
        id: log?.id,
        userId: log?.user_id,
        actionType: log?.action_type,
        resourceType: log?.resource_type,
        resourceId: log?.resource_id,
        changes: log?.changes,
        metadata: log?.metadata,
        ipAddress: log?.ip_address,
        userAgent: log?.user_agent,
        createdAt: log?.created_at,
        user: {
          id: log?.user?.id,
          email: log?.user?.email,
          firstName: log?.user?.raw_user_meta_data?.first_name,
          lastName: log?.user?.raw_user_meta_data?.last_name,
          userType: log?.user?.raw_user_meta_data?.user_type
        }
      }));
      
      return { data: transformedData, error: null };
    } catch (error) {
      logger?.error('Audit logs fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch audit logs' } };
    }
  },

  /**
   * Get audit statistics
   * @param {object} options - Query options
   * @returns {Promise<{data, error}>}
   */
  async getAuditStats(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }
      
      let query = supabase
        ?.from('audit_logs')
        ?.select('action_type, resource_type, created_at');
      
      // Apply date range filter
      if (options?.dateRange?.start) {
        query = query?.gte('created_at', options?.dateRange?.start);
      }
      if (options?.dateRange?.end) {
        query = query?.lte('created_at', options?.dateRange?.end);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger?.error('Failed to fetch audit stats', error, { requestId });
        return { data: null, error };
      }
      
      // Calculate statistics
      const stats = {
        totalEvents: data?.length || 0,
        byActionType: {},
        byResourceType: {},
        recentActivity: []
      };
      
      data?.forEach(log => {
        // Count by action type
        stats.byActionType[log?.action_type] = (stats?.byActionType?.[log?.action_type] || 0) + 1;
        
        // Count by resource type
        stats.byResourceType[log?.resource_type] = (stats?.byResourceType?.[log?.resource_type] || 0) + 1;
      });
      
      return { data: stats, error: null };
    } catch (error) {
      logger?.error('Audit stats fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch audit stats' } };
    }
  },

  /**
   * Export audit logs to CSV
   * @param {object} options - Export options
   * @returns {Promise<{data, error}>}
   */
  async exportAuditLogs(options = {}) {
    const { data: logs, error } = await this.getAuditLogs({ ...options, limit: 10000 });
    
    if (error) {
      return { data: null, error };
    }
    
    try {
      const headers = ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Changes'];
      const rows = logs?.map(log => [
        new Date(log?.createdAt)?.toLocaleString(),
        log?.user?.email || 'Unknown',
        log?.actionType,
        log?.resourceType,
        log?.resourceId || 'N/A',
        JSON.stringify(log?.changes)
      ]);
      
      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      return { data: { url, filename: `audit-logs-${new Date()?.toISOString()?.split('T')?.[0]}.csv` }, error: null };
    } catch (error) {
      logger?.error('Audit export error', error);
      return { data: null, error: { message: 'Failed to export audit logs' } };
    }
  }
};

export default auditService;